import _ from 'lodash';
import React from 'react';
import { Button, Form } from 'react-bootstrap';

import Checkbox from 'components/Checkbox';
import Icon from 'components/Icon';
import { IconType } from 'components/Icon/constants';
import { isVeevaMedcomms, ProjectType, UploadProjectType } from 'const';
import { UploadContextOptions } from 'models';
import { getBinder } from 'services/api';
import { intlGet } from 'utils/intlGet';
import { BinderFormState } from '../ManageBinder/binderFormReducer';
import { ProjectTypeSettings, UploadProjectProps, UploadProjectState } from './models';
import styles from './styles.module.scss';

const UploadProjectTypeSettings: Record<UploadProjectType, ProjectTypeSettings> = {
  [UploadProjectType.DRAFT]: {
    text: () => intlGet('UploadProjectWindow.Option', 'ReadyForReview'),
    completeTranslationDisabled: true,
  },
  [UploadProjectType.READY_FOR_REVIEW]: {
    text: () => intlGet('UploadProjectWindow.Option', 'Draft'),
    completeTranslationDisabled: false,
  },
};

const UploadButton: React.FC<{
  uploadProject: (binderForm?: BinderFormState | null) => React.MouseEventHandler;
}> = ({ uploadProject }) => {
  const buttonPrefix = 'UploadProjectWindow.Button';

  return (
    <Button
      onClick={uploadProject()}
      variant="primary"
    >
      {intlGet(buttonPrefix, 'Upload')}
    </Button>
  );
};

class UploadProject extends React.PureComponent<UploadProjectProps, UploadProjectState> {
  readonly state: UploadProjectState = {
    selectedUploadProjectType: UploadProjectType.DRAFT,
    translationComplete: false,
    isBinderAlreadyCreated: false,
    loadingGetLinkedBinders: false,
  };

  private toggleTranslationComplete = (): void => {
    this.setState(prevState => ({
      translationComplete: !prevState.translationComplete,
    }));
  };

  private onUploadProjectTypeChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const selectedUploadProjectType = event.target.name as UploadProjectType;
    const { completeTranslationDisabled } = UploadProjectTypeSettings[selectedUploadProjectType];

    this.setState(prevState => ({
      selectedUploadProjectType,
      translationComplete: !completeTranslationDisabled && prevState.translationComplete,
    }));
  };

  private uploadProject = (binderFormState?: BinderFormState): React.MouseEventHandler => () => {
    const { hideModal, uploadToPromoMats } = this.props;
    const { selectedUploadProjectType, translationComplete } = this.state;

    uploadToPromoMats(selectedUploadProjectType, translationComplete, binderFormState);
    hideModal();
  };

  private getLabel = (uploadProjectType: UploadProjectType): JSX.Element => {
    const { text } = UploadProjectTypeSettings[uploadProjectType];

    return (
      <div className={styles.formCheckLabel}>{text()}</div>
    );
  };

  private handleManageBinderModalOpen = (): void => {
    const { createBinderDuplicate, hideModal, showBinderModal } = this.props;
    const { isBinderAlreadyCreated, selectedUploadProjectType, translationComplete } = this.state;

    const uploadContext: UploadContextOptions = {
      uploadProjectType: selectedUploadProjectType,
      translationComplete,
    };

    if (isBinderAlreadyCreated) {
      createBinderDuplicate(uploadContext);
    } else {
      hideModal();
      showBinderModal({
        disableClickOutside: true,
        uploadContext,
      });
    }
  };

  checkIsBinderAlreadyCreated = async (): Promise<void> => {
    this.setState({
      loadingGetLinkedBinders: true,
    });

    try {
      const linkedBinders = await getBinder(this.props.rootDocumentId);

      this.setState({
        isBinderAlreadyCreated: linkedBinders.length > 0,
        loadingGetLinkedBinders: false,
      });
    } catch (error) {
      this.setState({
        isBinderAlreadyCreated: false,
        loadingGetLinkedBinders: false,
      });
    }
  };

  componentDidMount(): void {
    const { projectType } = this.props;
    const isBinderCreationEnabled = projectType === ProjectType.IPAD && !isVeevaMedcomms();
    if (isBinderCreationEnabled) {
      void this.checkIsBinderAlreadyCreated();
    }
  }

  render(): JSX.Element {
    const { selectedUploadProjectType, translationComplete } = this.state;
    const { hideModal, isProjectTranslatable, projectType } = this.props;
    const buttonPrefix = 'UploadProjectWindow.Button';
    const isBinderCreationEnabled = projectType === ProjectType.IPAD && !isVeevaMedcomms();

    return (
      <div className={styles.UploadProject}>
        <header>{intlGet('UploadProjectWindow', 'UploadToPromoMats')}</header>
        <section>
          <Form>
            <Form.Group>
              <Form.Label className={styles.formLabel}>{intlGet('UploadProjectWindow', 'SelectOption')}</Form.Label>
              {
                _.map(UploadProjectTypeSettings, (text, type: UploadProjectType) => {
                  return (
                    <Form.Check
                      type="radio"
                      key={type}
                      id={type}
                      label={this.getLabel(type)}
                      name={type}
                      checked={type === selectedUploadProjectType}
                      onChange={this.onUploadProjectTypeChange}
                      className={styles.formCheck}
                    />
                  );
                })
              }
            </Form.Group>
            {isProjectTranslatable && <Checkbox
              className={styles.checkbox}
              onChange={this.toggleTranslationComplete}
              checked={translationComplete}
              disabled={UploadProjectTypeSettings[selectedUploadProjectType].completeTranslationDisabled}
              label={intlGet('UploadProjectWindow.Translation', 'Complete')}
              comments={intlGet('UploadProjectWindow.Translation', 'Comments')}
            />}
          </Form>
        </section>
        <footer>
          <div className={styles.mainButtons}>
            <Button onClick={hideModal} variant="secondary">{intlGet(buttonPrefix, 'Cancel')}</Button>
            <UploadButton uploadProject={this.uploadProject} />
          </div>
          {
            isBinderCreationEnabled ?
              <Button
                onClick={this.handleManageBinderModalOpen}
                variant='secondary'
                className={styles.manageBinder}
                disabled={this.state.loadingGetLinkedBinders}
              >
                <Icon type={IconType.NEW_FILE} size='sm' color='inherit' />
                {intlGet(buttonPrefix, 'UploadAndCreateBinder')}
              </Button> : null
          }
        </footer>
      </div>
    );
  }
}

export default UploadProject;
